import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTaxProfile } from '../hooks/useTaxProfile'
import { useSubject } from '../context/SubjectContext'
import { calcProfileDepth } from '../lib/profileUtils'
import { getSlotOptions } from '../lib/slotConfig'
import posthog from '../lib/posthog'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getSlot(profile, slotId) {
  return profile?.profile_data?.slots?.[slotId]?.value ?? null
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

function parseMoveDate(moveDate) {
  if (!moveDate) return { move_month: '', move_year: '', move_day: '' }
  const [year, month, day] = moveDate.split('-')
  return {
    move_year:  year  || '',
    move_month: month ? String(parseInt(month, 10)) : '',
    move_day:   day   ? String(parseInt(day,   10)) : '1',
  }
}

function deriveResidencyYearType(moveDate, activeTaxYear) {
  if (!moveDate || !activeTaxYear) return null
  const moveYear = new Date(moveDate).getFullYear()
  if (moveYear === activeTaxYear) return 'split_year'
  if (moveYear < activeTaxYear)  return 'full_year_spain'
  return null
}

function deriveSpainResidencyStatus(moveDate, residencyYearType, activeTaxYear) {
  if (!activeTaxYear) return null
  if (residencyYearType === 'full_year_spain') return 'resident_full_year'
  if (residencyYearType === 'split_year' && moveDate) {
    const arrival     = new Date(moveDate)
    const yearEnd     = new Date(`${activeTaxYear}-12-31`)
    const daysInSpain = Math.floor((yearEnd - arrival) / 86400000) + 1
    return daysInSpain >= 183 ? 'resident_partial_year' : 'non_resident'
  }
  if (residencyYearType === 'full_year_us') return 'non_resident'
  return null
}

function profileToForm(p, y, activeTaxYear) {
  return {
    citizenship_status:  p?.citizenship_status || '',
    beckham_status:      p?.beckham_status     || '',
    comunidad_autonoma:  p?.comunidad_autonoma || getSlot(p, 'comunidad_autonoma') || '',
    ...parseMoveDate(p?.move_date || (() => {
      const legacy = getSlot(p, 'move_timing')
      if (!legacy) return null
      const m = legacy.match(/(\w+)\s+(\d{4})/)
      if (!m) return null
      const monthIdx = MONTHS.findIndex(mn => mn.toLowerCase() === m[1].toLowerCase())
      if (monthIdx === -1) return null
      return `${m[2]}-${String(monthIdx + 1).padStart(2, '0')}-01`
    })()),
    marital_status:      p?.marital_status     || getSlot(p, 'marital_status')     || '',
    asset_split:         p?.asset_split        || getSlot(p, 'asset_split')        || '',
    foreign_asset_types: getSlot(p, 'foreign_asset_types') || [],
    property_ownership:  getSlot(p, 'property_ownership')  || [],
    net_spanish_assets:  getSlot(p, 'net_spanish_assets')  || '',
    mortgage_debt:       getSlot(p, 'mortgage_debt')       || '',
    wealth_tax_exposure: getSlot(p, 'wealth_tax_exposure') || '',

    residency_year_type:             y?.residency_year_type || p?.residency_year_type || deriveResidencyYearType(p?.move_date, activeTaxYear) || '',
    spain_residency_status:          y?.spain_residency_status || p?.spain_residency_status || '',
    days_in_spain:                   y?.days_in_spain != null ? String(y.days_in_spain) : (getSlot(p, 'days_in_spain') || ''),
    foreign_account_band:            y?.foreign_account_band            || p?.foreign_account_band     || '',
    income_types:                    y?.income_types                    || getSlot(p, 'income_types')  || p?.income_types || [],
    major_events:                    y?.major_events                    || getSlot(p, 'major_events')  || p?.major_events || [],
    rental_property_location:        y?.rental_property_location        || getSlot(p, 'rental_property_location')        || '',
    rental_property_sold:            y?.rental_property_sold            || getSlot(p, 'rental_property_sold')            || '',
    investment_account_location:     y?.investment_account_location     || getSlot(p, 'investment_account_location')     || '',
    employment_location_when_earned: y?.employment_location_when_earned || getSlot(p, 'employment_location_when_earned') || '',
    feie_or_ftc_preference:          y?.feie_ftc_preference             || getSlot(p, 'feie_or_ftc_preference')          || '',
    self_employment_country:         y?.self_employment_country         || getSlot(p, 'self_employment_country')         || '',
    totalization_status:             y?.totalization_status             || getSlot(p, 'totalization_status')             || '',
    property_sale_location:          y?.property_sale_location          || getSlot(p, 'property_sale_location')          || '',
    retirement_account_types:        y?.retirement_account_types        || getSlot(p, 'retirement_account_types')        || [],
    filing_goal:                     y?.filing_goal                     || getSlot(p, 'filing_goal')                     || '',
    urgency_deadline:                y?.urgency_deadline                || getSlot(p, 'urgency_deadline')                || '',
    prior_year_issues:               y?.prior_year_issues               || getSlot(p, 'prior_year_issues')               || '',
    income_estimates:                y?.year_data?.income_estimates     || {},
  }
}

function formToPayloads(form, activeTaxYear) {
  const now = new Date().toISOString()
  const slot = (value) => ({
    value,
    status: value !== '' && (Array.isArray(value) ? value.length > 0 : true) ? 'known' : 'unknown',
    source: 'profile',
    updated_at: now,
  })

  const move_date = (form.move_month && form.move_year)
    ? (() => {
        const month = String(form.move_month).padStart(2, '0')
        const day   = String(form.move_month === '7' ? (form.move_day || 1) : 1).padStart(2, '0')
        return `${form.move_year}-${month}-${day}`
      })()
    : null

  const stablePayload = {
    citizenship_status: form.citizenship_status || null,
    beckham_status:     form.beckham_status     || null,
    // triage_completed lives on tax_year_profiles only
    move_date,
    profile_data: {
      slots: {
        comunidad_autonoma:  form.comunidad_autonoma || null,
        move_timing:         slot(move_date ? `${MONTHS[parseInt(form.move_month, 10) - 1]} ${form.move_year}` : ''),
        marital_status:      form.marital_status     || null,
        asset_split:         form.asset_split        || null,
        foreign_asset_types: slot(form.foreign_asset_types),
        property_ownership:  slot(form.property_ownership),
        net_spanish_assets:  slot(form.net_spanish_assets),
        mortgage_debt:       slot(form.mortgage_debt),
        wealth_tax_exposure: slot(form.wealth_tax_exposure),
      }
    },
  }

  const move_date_for_derive = (form.move_month && form.move_year)
    ? `${form.move_year}-${String(form.move_month).padStart(2,'0')}-${String(form.move_month === '7' ? (form.move_day || 1) : 1).padStart(2,'0')}`
    : null

  const derivedResidencyYearType = deriveResidencyYearType(move_date_for_derive, activeTaxYear)
  const derivedSpainResidency    = deriveSpainResidencyStatus(move_date_for_derive, form.residency_year_type || derivedResidencyYearType, activeTaxYear)

  const yearPayload = {
    residency_year_type:    form.residency_year_type    || derivedResidencyYearType || null,
    spain_residency_status: form.spain_residency_status || derivedSpainResidency    || null,
    days_in_spain:          form.days_in_spain ? parseInt(form.days_in_spain, 10) : null,    foreign_account_band:            form.foreign_account_band            || null,
    income_types:                    Array.isArray(form.income_types)             ? form.income_types             : [],
    major_events:                    Array.isArray(form.major_events)             ? form.major_events             : [],
    rental_property_location:        form.rental_property_location        || null,
    rental_property_sold:            form.rental_property_sold            || null,
    investment_account_location:     form.investment_account_location     || null,
    employment_location_when_earned: form.employment_location_when_earned || null,
    feie_ftc_preference:             form.feie_or_ftc_preference          || null,
    self_employment_country:         form.self_employment_country         || null,
    totalization_status:             form.totalization_status             || null,
    property_sale_location:          form.property_sale_location          || null,
    retirement_account_types:        Array.isArray(form.retirement_account_types) ? form.retirement_account_types : [],
    filing_goal:                     form.filing_goal                     || null,
    urgency_deadline:                form.urgency_deadline                || null,
    prior_year_issues:               form.prior_year_issues               || null,
    triage_completed:                !!(form.residency_year_type && form.spain_residency_status),
    year_data: {
      income_estimates: form.income_estimates || {},
    },
  }

  return { stablePayload, yearPayload }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CONSENT_LABELS = {
  self_entered:      'Self-entered',
  advisor_entered:   'Advisor entered',
  informal_summary:  'Informal summary',
  client_authorized: 'Client authorized',
}

const TYPE_LABELS = {
  self:          'Your profile',
  client:        'Client profile',
  prospect:      'Prospect profile',
  family_member: 'Family member profile',
}

const sections = [
  { id: 'identity',         label: 'Identity & residency' },
  { id: 'income',           label: 'Income' },
  { id: 'income-estimates', label: 'Income estimates' },
  { id: 'assets',           label: 'Assets & accounts' },
  { id: 'events',           label: 'Major events' },
  { id: 'regime',           label: 'Tax regime' },
  { id: 'goals',            label: 'Goals & deadlines' },
]

const THIS_YEAR = new Date().getFullYear()
const TAX_YEARS = [THIS_YEAR + 1, THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2, THIS_YEAR - 3].filter(y => y >= 2022)

function asArray(value) {
  return Array.isArray(value) ? value : []
}

// ── Shared UI components ───────────────────────────────────────────────────────

function Pill({ children, tone = 'neutral' }) {
  const cls = { neutral: 'pill-neutral', teal: 'pill-teal', amber: 'pill-amber', red: 'pill-red' }
  return <span className={`pill ${cls[tone] || 'pill-neutral'}`}>{children}</span>
}

function Section({ id, eyebrow, title, description, children }) {
  return (
    <section id={id} className="profile-section">
      <div className="profile-section-header">
        {eyebrow && <div className="profile-section-eyebrow">{eyebrow}</div>}
        <h2 className="profile-section-title">{title}</h2>
        {description && <p className="profile-section-desc">{description}</p>}
      </div>
      {children}
    </section>
  )
}

function SectionNav() {
  return (
    <aside className="profile-section-nav">
      <div className="profile-section-nav-label">Sections</div>
      <nav>
        {sections.map(s => (
          <a key={s.id} href={`#${s.id}`} className="profile-section-nav-link">{s.label}</a>
        ))}
      </nav>
    </aside>
  )
}

function SelectField({ label, value, options, onChange, helper, placeholder = '— select —' }) {
  return (
    <label className="block">
      <div className="u-form-label">{label}</div>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="profile-select"
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {helper && <div className="profile-field-helper">{helper}</div>}
    </label>
  )
}

function TextAreaField({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <div className="u-form-label">{label}</div>
      <textarea
        className="profile-textarea"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  )
}

function CheckboxCard({ checked, label, onChange, children }) {
  return (
    <div className={`profile-checkbox-card${checked ? ' checked' : ''}`}>
      <label className="profile-checkbox-label">
        <input
          type="checkbox"
          checked={Boolean(checked)}
          onChange={e => onChange(e.target.checked)}
        />
        <span className="profile-checkbox-text">{label}</span>
      </label>
      {checked && children
        ? <div className="profile-checkbox-children">{children}</div>
        : null
      }
    </div>
  )
}

function CheckboxGrid({ label, children, helper }) {
  return (
    <div>
      {label && <div className="profile-checkbox-grid-label">{label}</div>}
      <div className="profile-checkbox-grid">{children}</div>
      {helper && <div className="profile-field-helper">{helper}</div>}
    </div>
  )
}

function TaxYearSwitcher({ activeTaxYear, setActiveTaxYear }) {
  return (
    <div className="profile-year-switcher">
      <span className="profile-year-label">Tax year</span>
      <select
        className="profile-year-select"
        value={activeTaxYear || ''}
        onChange={e => setActiveTaxYear(parseInt(e.target.value, 10))}
      >
        {TAX_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  )
}

// ── ProfilePage ───────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { taxProfile, yearProfile, loading, saving, saveProfile } = useTaxProfile()
  const { activeSubject, activeTaxYear, setActiveTaxYear } = useSubject()

  const [form, setForm]           = useState({})
  const [saved, setSaved]         = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    setForm(profileToForm(taxProfile, yearProfile, activeTaxYear))
    setSaved(false)
    setSaveError(null)
  }, [taxProfile, yearProfile, activeSubject?.id, activeTaxYear])

  function set(key, val) { setForm(f => ({ ...f, [key]: val })) }

  function setEstimate(field, value) {
    setForm(f => ({ ...f, income_estimates: { ...(f.income_estimates || {}), [field]: value } }))
  }

  function toggleMulti(key, value, noneValue) {
    setForm(f => {
      const current = f[key] || []
      if (value === noneValue) return { ...f, [key]: [noneValue] }
      const filtered = current.filter(v => v !== noneValue)
      if (filtered.includes(value)) return { ...f, [key]: filtered.filter(v => v !== value) }
      return { ...f, [key]: [...filtered, value] }
    })
  }

  async function handleSave() {
    setSaved(false)
    setSaveError(null)
    const { stablePayload, yearPayload } = formToPayloads(form, activeTaxYear)

    console.log('[ProfilePage] activeTaxYear at save time:', activeTaxYear)
    console.log('[ProfilePage] yearPayload:', JSON.stringify(yearPayload, null, 2))
    const { error } = await saveProfile({ stablePayload, yearPayload })
    console.log('[ProfilePage] save result error:', error)
    if (error) {
      setSaveError('Save failed — ' + error.message)
    } else {
      posthog.capture('profile_saved', {
        tax_year: activeTaxYear,
        profile_depth: profileDepth,
        subject_type: activeSubject?.subject_type || 'self',
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    }
  }

  // Inner components that close over form state
  function Select({ fieldKey, options, placeholder = '— select —', label, helper }) {
    return (
      <SelectField
        label={label}
        value={form[fieldKey] || ''}
        options={options}
        placeholder={placeholder}
        helper={helper}
        onChange={value => set(fieldKey, value)}
      />
    )
  }

  function Check({ fieldKey, value, label, noneValue, children }) {
    const current = asArray(form[fieldKey])
    return (
      <CheckboxCard
        checked={current.includes(value)}
        label={label}
        onChange={() => toggleMulti(fieldKey, value, noneValue)}
      >
        {children}
      </CheckboxCard>
    )
  }

  const isNonSelf         = activeSubject && activeSubject.subject_type !== 'self'
  const pageTitle         = activeSubject ? (TYPE_LABELS[activeSubject.subject_type] || 'Tax Profile') : 'Tax Profile'
  const subjectName       = isNonSelf ? activeSubject.display_name : null
  const they              = isNonSelf ? 'they' : 'you'
  const their             = isNonSelf ? 'their' : 'your'
  const incomeTypes       = asArray(form.income_types)
  const majorEvents       = asArray(form.major_events)
  const complexityTier    = yearProfile?.complexity_tier || taxProfile?.complexity_tier

  // Use shared utility — form shape is compatible
  const profileDepth = calcProfileDepth({
    ...form,
    income_types: incomeTypes,
    major_events: majorEvents,
  })

  if (loading) {
    return (
      <div className="profile-loading">
        Loading {subjectName ? `${subjectName}'s` : 'your'} profile…
      </div>
    )
  }

      if (loading) return <div className="asmt-state"><div className="app-spinner" /></div>

  return (

    <div className="profile-page">

      {/* ── Page header ── */}
      <div className="profile-page-header">
        <div className="profile-page-header-inner">

          <div className="profile-page-topnav">
            <Link to="/chat" className="u-text-link">← Back to chat</Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {activeTaxYear && (
                <TaxYearSwitcher activeTaxYear={activeTaxYear} setActiveTaxYear={setActiveTaxYear} />
              )}
              {isNonSelf && (
                <Link to="/subjects" className="u-text-link">Manage subjects →</Link>
              )}
            </div>
          </div>

          <div className="profile-heading-row">
            <div>
              <div className="profile-title-pills">
                <h1 className="profile-title">{pageTitle}</h1>
                {activeTaxYear && <Pill tone="teal">{activeTaxYear}</Pill>}
                {complexityTier && (
                  <Pill tone={complexityTier === 'complex' ? 'red' : complexityTier === 'moderate' ? 'amber' : 'teal'}>
                    {complexityTier.charAt(0).toUpperCase() + complexityTier.slice(1)}
                  </Pill>
                )}
              </div>
              <p className="profile-subtitle">
                {isNonSelf
                  ? `Editing profile for ${subjectName}. This information personalises assessments and chat for this subject.`
                  : 'This is the source of truth for chat and assessment. Fill in what you know now — you can refine it later.'}
              </p>
            </div>

            <div style={{fontSize: '11px', color: 'red', padding: '4px'}}>

          </div>

            <div className="profile-depth-card">
              <div className="profile-depth-row">
                <span>Profile depth</span>
                <span className="profile-depth-pct">{profileDepth}%</span>
              </div>
              <div className="profile-depth-track">
                <div className="u-progress-fill" style={{ width: `${profileDepth}%` }} />
              </div>
              <div className="profile-depth-hint">
                Deeper profiles produce better assessments and more grounded chat answers.
              </div>
            </div>
          </div>

          {isNonSelf && (
            <div className="profile-subject-banner">
              <div className="u-title-sm">{activeSubject.display_name}</div>
              <div className="profile-subject-meta">
                {TYPE_LABELS[activeSubject.subject_type]}
                {activeSubject.consent_status && ` · ${CONSENT_LABELS[activeSubject.consent_status] || activeSubject.consent_status}`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main content ── */}
      <main className="profile-main">
        <SectionNav />

        <div className="u-stack-xl">

          {!taxProfile && !yearProfile && !loading && (
            <div className="profile-no-profile">
              No profile exists yet for {subjectName || 'this subject'}. Fill in the fields below and save to create one.
              {isNonSelf && ' Fill in at least the residency and citizenship fields to unlock assessment generation.'}
            </div>
          )}

          {/* ── Step 1: Identity ── */}
          <Section
            id="identity"
            eyebrow="Step 1"
            title="Identity, residency & household"
            description="Start with the facts that determine which tax systems may apply. Marital status belongs here because it affects Spain-side filing and asset thresholds."
          >
            <div className="profile-grid-2">
              <Select label="US status" fieldKey="citizenship_status" options={getSlotOptions('citizenship_status')} />
              <Select label={`Where did ${they} live in ${activeTaxYear || 'this year'}?`} fieldKey="residency_year_type" options={getSlotOptions('residency_year_type')} />
              <SelectField
                label="Spanish tax residency"
                value={form.spain_residency_status || deriveSpainResidencyStatus(
                  form.move_month && form.move_year
                    ? `${form.move_year}-${String(form.move_month).padStart(2,'0')}-${String(form.move_month === '7' ? (form.move_day || 1) : 1).padStart(2,'0')}`
                    : null,
                  form.residency_year_type || deriveResidencyYearType(
                    form.move_month && form.move_year
                      ? `${form.move_year}-${String(form.move_month).padStart(2,'0')}-01`
                      : null,
                    activeTaxYear
                  ),
                  activeTaxYear
                ) || ''}
                options={[
                  { value: 'resident_full_year',    label: '🇪🇸 Resident — full year' },
                  { value: 'resident_partial_year', label: '🇪🇸 Resident — part of year' },
                  { value: 'non_resident',          label: 'Not a resident' },
                  { value: 'uncertain',             label: 'Not sure' },
                ]}
                onChange={value => set('spain_residency_status', value)}
                helper="Auto-derived from move date — override if needed."
              />
              <label className="block">
                <div className="u-form-label">Comunidad autónoma</div>
                <select
                  className="profile-select"
                  value={form.comunidad_autonoma || ''}
                  onChange={e => set('comunidad_autonoma', e.target.value)}
                >
                  <option value="">— select —</option>
                  {getSlotOptions('comunidad_autonoma').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>

              <div className="profile-col-full">
                <div className="u-form-label">When did {they} move to Spain?</div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <select
                    value={form.move_month || ''}
                    onChange={e => set('move_month', e.target.value)}
                    className="profile-select"
                    style={{ flex: 1 }}
                  >
                    <option value="">Month</option>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={String(i + 1)}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={form.move_year || ''}
                    onChange={e => set('move_year', e.target.value)}
                    className="profile-select"
                    style={{ width: '7rem' }}
                  >
                    <option value="">Year</option>
                    {[2020,2021,2022,2023,2024,2025,2026].map(y => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                  {form.move_month === '7' && (
                    <select
                      value={form.move_day || ''}
                      onChange={e => set('move_day', e.target.value)}
                      className="profile-select"
                      style={{ width: '6rem' }}
                    >
                      <option value="">Day</option>
                      {DAYS.map(d => (
                        <option key={d} value={String(d)}>{d}</option>
                      ))}
                    </select>
                  )}
                </div>
                {form.move_month === '7' && (
                  <div className="profile-field-helper">
                    If you moved in July, the exact day determines which side of the 183-day residency threshold you fall on.
                  </div>
                )}
              </div>

              <Select
                label="Marital status"
                fieldKey="marital_status"
                helper="Spain requires individual filing — no joint returns for IRPF, patrimonio, or Modelos."
                options={getSlotOptions('marital_status')}
              />
              {(form.marital_status === 'married' || form.marital_status === 'civil_partnership') && (
                <Select
                  label="Asset distribution between spouses"
                  fieldKey="asset_split"
                  helper="The €3M ITSGF threshold applies per person — asset titling between spouses is significant."
                  options={[
                    { value: 'roughly_equal',   label: 'Roughly equal between both' },
                    { value: 'one_spouse_more', label: 'One spouse holds significantly more' },
                    { value: 'all_one_spouse',  label: "Almost all in one spouse's name" },
                    { value: 'not_sure',        label: 'Not sure how assets are titled' },
                  ]}
                />
              )}
            </div>
          </Section>

          {/* ── Step 2: Income ── */}
          <Section
            id="income"
            eyebrow="Step 2"
            title={`Income in ${activeTaxYear || 'this year'}`}
            description="Income is grouped by source so related follow-up questions appear next to the answers that triggered them."
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

              <div>
                <h3 className="profile-subheading">Income from work</h3>
                <CheckboxGrid>
                  <Check fieldKey="income_types" value="employment_us" noneValue="none" label="🇺🇸 US employment (W-2)">
                    <Select label="Where was employment income earned?" fieldKey="employment_location_when_earned" options={getSlotOptions('employment_location_when_earned')} />
                    {form.employment_location_when_earned && form.employment_location_when_earned !== 'us_only' && (
                      <div style={{ marginTop: '1rem' }}>
                        <Select
                          label="FEIE vs FTC preference"
                          fieldKey="feie_or_ftc_preference"
                          helper="FTC is generally preferred for Spanish residents due to higher effective tax rates."
                          options={[
                            { value: 'feie',           label: 'Foreign Earned Income Exclusion (FEIE)' },
                            { value: 'ftc',            label: 'Foreign Tax Credit (FTC)' },
                            { value: 'undecided',      label: 'Not sure — need guidance' },
                            { value: 'not_applicable', label: 'Not applicable' },
                          ]}
                        />
                      </div>
                    )}
                  </Check>
                  <Check fieldKey="income_types" value="employment_es" noneValue="none" label="🇪🇸 Spanish employment" />
                  <Check fieldKey="income_types" value="self_employment" noneValue="none" label="Self-employment / freelance">
                    <div className="profile-grid-2">
                      <Select label="Self-employment location" fieldKey="self_employment_country" options={getSlotOptions('self_employment_country')} />
                      <Select
                        label="Social Security / totalization"
                        fieldKey="totalization_status"
                        helper="Autónomos paying Spanish social security are typically exempt from US SE tax under the totalization agreement."
                        options={[
                          { value: 'spain_social_security',    label: '🇪🇸 Paying Spanish social security / autónomo' },
                          { value: 'us_self_employment_tax',   label: '🇺🇸 Paying US self-employment tax' },
                          { value: 'totalization_certificate', label: 'Have a totalization certificate' },
                          { value: 'unsure',                   label: 'Not sure' },
                        ]}
                      />
                    </div>
                  </Check>
                </CheckboxGrid>
              </div>

              <div>
                <h3 className="profile-subheading">Income from property</h3>
                <CheckboxGrid>
                  <Check fieldKey="income_types" value="rental" noneValue="none" label="Rental income">
                    <div className="profile-grid-2">
                      <Select label="Rental property location" fieldKey="rental_property_location" options={getSlotOptions('rental_property_location')} />
                      <Select label={`Did you sell the rental property in ${activeTaxYear || 'this year'}?`} fieldKey="rental_property_sold" options={[
                        { value: 'yes', label: 'Yes — sold this year' },
                        { value: 'no',  label: 'No — still own it' },
                      ]} />
                    </div>
                  </Check>
                </CheckboxGrid>
              </div>

              <div>
                <h3 className="profile-subheading">Income from investments & retirement</h3>
                <CheckboxGrid>
                  <Check fieldKey="income_types" value="investments" noneValue="none" label="Investments / dividends / capital gains">
                    <Select label="Where are your investment accounts primarily held?" fieldKey="investment_account_location" options={[
                      { value: 'us',    label: '🇺🇸 US brokerage account' },
                      { value: 'spain', label: '🇪🇸 Spanish account' },
                      { value: 'both',  label: '🇺🇸 + 🇪🇸 Both' },
                      { value: 'other', label: '🌍 Another country' },
                    ]} />
                  </Check>
                  <Check fieldKey="income_types" value="pension" noneValue="none" label="Pension or retirement withdrawals">
                    <CheckboxGrid label="Retirement account types">
                      {[
                        { value: '401k',                    label: '401(k)' },
                        { value: 'ira_traditional',         label: 'Traditional IRA' },
                        { value: 'ira_roth',                label: 'Roth IRA' },
                        { value: 'pension_defined_benefit', label: 'Defined benefit pension' },
                        { value: 'other',                   label: 'Other' },
                      ].map(o => (
                        <Check key={o.value} fieldKey="retirement_account_types" value={o.value} label={o.label} />
                      ))}
                    </CheckboxGrid>
                  </Check>
                  <Check fieldKey="income_types" value="none" noneValue="none" label="None of the above" />
                </CheckboxGrid>
              </div>

            </div>
          </Section>

          {/* ── Step 2b: Income estimates ── */}
          <Section
            id="income-estimates"
            eyebrow="Step 2b"
            title={`Approximate income amounts for ${activeTaxYear || 'this year'}`}
            description="Rough bands only — used to orient your Spain tax exposure estimate. Exact figures are not required and will not affect your assessment narrative."
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

              {(incomeTypes.includes('employment_us') || incomeTypes.includes('employment_es')) && (
                <SelectField
                  label="Employment income (salary, wages, bonuses)"
                  value={form.income_estimates?.employment_band || ''}
                  options={[
                    { value: 'none',              label: 'None / not applicable' },
                    { value: 'under_25k',         label: 'Under €25,000' },
                    { value: '25k_50k',           label: '€25,000 – €50,000' },
                    { value: '50k_75k',           label: '€50,000 – €75,000' },
                    { value: '75k_100k',          label: '€75,000 – €100,000' },
                    { value: '100k_150k',         label: '€100,000 – €150,000' },
                    { value: '150k_250k',         label: '€150,000 – €250,000' },
                    { value: '250k_plus',         label: 'Over €250,000' },
                    { value: 'unknown',           label: "I'm not sure" },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  helper="Combined total from all employment sources before Spanish tax."
                  onChange={value => setEstimate('employment_band', value)}
                />
              )}

              {incomeTypes.includes('self_employment') && (
                <SelectField
                  label="Self-employment / autónomo net income"
                  value={form.income_estimates?.self_employment_band || ''}
                  options={[
                    { value: 'none',              label: 'None / not applicable' },
                    { value: 'under_15k',         label: 'Under €15,000' },
                    { value: '15k_40k',           label: '€15,000 – €40,000' },
                    { value: '40k_75k',           label: '€40,000 – €75,000' },
                    { value: '75k_150k',          label: '€75,000 – €150,000' },
                    { value: '150k_plus',         label: 'Over €150,000' },
                    { value: 'unknown',           label: "I'm not sure" },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  helper="Net of deductible business expenses — not gross revenue."
                  onChange={value => setEstimate('self_employment_band', value)}
                />
              )}

              {incomeTypes.includes('rental') && (
                <SelectField
                  label="Net rental income"
                  value={form.income_estimates?.rental_band || ''}
                  options={[
                    { value: 'none',              label: 'None / not applicable' },
                    { value: 'under_10k',         label: 'Under €10,000' },
                    { value: '10k_25k',           label: '€10,000 – €25,000' },
                    { value: '25k_50k',           label: '€25,000 – €50,000' },
                    { value: '50k_100k',          label: '€50,000 – €100,000' },
                    { value: '100k_plus',         label: 'Over €100,000' },
                    { value: 'unknown',           label: "I'm not sure" },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  helper="After deductible property expenses — mortgage interest, maintenance, insurance, etc."
                  onChange={value => setEstimate('rental_band', value)}
                />
              )}

              {incomeTypes.includes('investments') && (
                <SelectField
                  label="Dividends and interest"
                  value={form.income_estimates?.dividends_band || ''}
                  options={[
                    { value: 'none',              label: 'None / not applicable' },
                    { value: 'under_1k',          label: 'Under €1,000' },
                    { value: '1k_5k',             label: '€1,000 – €5,000' },
                    { value: '5k_25k',            label: '€5,000 – €25,000' },
                    { value: '25k_100k',          label: '€25,000 – €100,000' },
                    { value: '100k_plus',         label: 'Over €100,000' },
                    { value: 'unknown',           label: "I'm not sure" },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  helper="Income from brokerage accounts, funds, bank interest, and similar sources."
                  onChange={value => setEstimate('dividends_band', value)}
                />
              )}

              {(incomeTypes.includes('investments') || majorEvents.includes('large_investment_sale') || majorEvents.includes('sold_property')) && (
                <SelectField
                  label="Capital gains"
                  value={form.income_estimates?.capital_gains_band || ''}
                  options={[
                    { value: 'none',              label: 'None / not applicable' },
                    { value: 'under_5k',          label: 'Under €5,000' },
                    { value: '5k_25k',            label: '€5,000 – €25,000' },
                    { value: '25k_100k',          label: '€25,000 – €100,000' },
                    { value: '100k_500k',         label: '€100,000 – €500,000' },
                    { value: '500k_plus',         label: 'Over €500,000' },
                    { value: 'unknown',           label: "I'm not sure" },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  helper="Net gains from selling investments, property, or other assets during the year."
                  onChange={value => setEstimate('capital_gains_band', value)}
                />
              )}

              {!incomeTypes.includes('employment_us') &&
               !incomeTypes.includes('employment_es') &&
               !incomeTypes.includes('self_employment') &&
               !incomeTypes.includes('rental') &&
               !incomeTypes.includes('investments') &&
               !majorEvents.includes('large_investment_sale') &&
               !majorEvents.includes('sold_property') && (
                <p className="profile-field-helper" style={{ fontStyle: 'italic' }}>
                  Complete the Income section above first — estimate fields will appear based on your income types.
                </p>
              )}

              <p className="profile-field-helper">
                These bands are used only to produce an orientation estimate of your Spanish IRPF exposure.
                "I'm not sure" and "Prefer not to say" are valid answers and will be reflected in the confidence level shown.
              </p>

            </div>
          </Section>

          {/* ── Step 3: Assets ── */}
          <Section id="assets" eyebrow="Step 3" title="Assets & accounts" description="These fields drive FBAR, FATCA, Modelo 720, wealth tax, and solidarity tax analysis.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="profile-grid-2">
                <Select
                  label="Financial accounts outside Spain"
                  fieldKey="foreign_account_band"
                  helper="As a Spanish resident, foreign accounts count toward worldwide patrimonio and ITSGF assessment."
                  options={[
                    { value: 'none',         label: 'None' },
                    { value: 'under_10k',    label: 'Under $10,000' },
                    { value: 'band_10_50k',  label: '$10,000 – $50,000' },
                    { value: 'band_50_200k', label: '$50,000 – $200,000' },
                    { value: 'band_200k_1m', label: '$200,000 – $1,000,000' },
                    { value: 'over_1m',      label: 'Over $1,000,000' },
                  ]}
                />
                <Select label="Estimated Spanish net assets" fieldKey="wealth_tax_exposure" options={[
                  { value: 'likely_none', label: 'Under €700,000' },
                  { value: 'possible',    label: '€700,000 – €3,000,000' },
                  { value: 'likely',      label: 'Over €3,000,000' },
                  { value: 'unsure',      label: 'Not sure' },
                ]} />
              </div>

              {form.foreign_account_band && form.foreign_account_band !== 'none' && (
                <CheckboxGrid label="Types of foreign accounts held">
                  {[
                    { value: 'us_brokerage',  label: '🇺🇸 US brokerage' },
                    { value: 'us_retirement', label: '🇺🇸 US retirement (401k/IRA)' },
                    { value: 'us_bank',       label: '🇺🇸 US bank account' },
                    { value: 'crypto',        label: 'Cryptocurrency' },
                    { value: 'foreign_bank',  label: '🌍 Non-US, non-Spain bank' },
                    { value: 'other',         label: 'Other' },
                  ].map(o => (
                    <Check key={o.value} fieldKey="foreign_asset_types" value={o.value} label={o.label} />
                  ))}
                </CheckboxGrid>
              )}

              <CheckboxGrid label="Property ownership">
                <Check fieldKey="property_ownership" value="spain_property" noneValue="none" label="🇪🇸 Property in Spain">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Select
                      label="Net Spanish assets (after mortgage)"
                      fieldKey="net_spanish_assets"
                      helper="Used to assess patrimonio and ITSGF exposure."
                      options={[
                        { value: 'under_300k',    label: 'Under €300,000' },
                        { value: 'band_300_700k', label: '€300,000 – €700,000' },
                        { value: 'band_700k_3m',  label: '€700,000 – €3,000,000' },
                        { value: 'over_3m',       label: 'Over €3,000,000' },
                        { value: 'unsure',        label: 'Not sure' },
                      ]}
                    />
                    <Select
                      label="Outstanding mortgage debt"
                      fieldKey="mortgage_debt"
                      helper="Mortgage debt reduces your taxable patrimonio base."
                      options={[
                        { value: 'none',          label: 'No mortgage' },
                        { value: 'under_200k',    label: 'Under €200,000' },
                        { value: 'band_200_500k', label: '€200,000 – €500,000' },
                        { value: 'over_500k',     label: 'Over €500,000' },
                        { value: 'unsure',        label: 'Not sure' },
                      ]}
                    />
                  </div>
                </Check>
                <Check fieldKey="property_ownership" value="us_property"    noneValue="none" label="🇺🇸 Property in the US" />
                <Check fieldKey="property_ownership" value="other_property" noneValue="none" label="🌍 Property in another country" />
                <Check fieldKey="property_ownership" value="none"           noneValue="none" label="No property owned" />
              </CheckboxGrid>
            </div>
          </Section>

          {/* ── Step 4: Events ── */}
          <Section id="events" eyebrow="Step 4" title={`Major events in ${activeTaxYear || 'this year'}`} description="Major life and asset events often create the issues that matter most in the assessment.">
            <CheckboxGrid>
              <Check fieldKey="major_events" value="moved_countries"       noneValue="none" label="Moved between countries" />
              <Check fieldKey="major_events" value="sold_property"         noneValue="none" label="Sold a property">
                <CheckboxGrid label="What type of property was sold?">
                  {[
                    { value: 'us_primary_residence',       label: '🇺🇸 US primary residence' },
                    { value: 'us_rental_or_investment',    label: '🇺🇸 US rental or investment property' },
                    { value: 'spain_primary_residence',    label: '🇪🇸 Spanish primary residence' },
                    { value: 'spain_rental_or_investment', label: '🇪🇸 Spanish rental or investment property' },
                  ].map(o => (
                    <Check key={o.value} fieldKey="property_sale_details" value={o.value} label={o.label} />
                  ))}
                </CheckboxGrid>
              </Check>
              <Check fieldKey="major_events" value="bought_property"       noneValue="none" label="Bought a property" />
              <Check fieldKey="major_events" value="started_business"      noneValue="none" label="Started / closed a business" />
              <Check fieldKey="major_events" value="retirement_withdrawal" noneValue="none" label="Took a retirement withdrawal" />
              <Check fieldKey="major_events" value="large_investment_sale" noneValue="none" label="Sold a large investment position" />
              <Check fieldKey="major_events" value="none"                  noneValue="none" label="None of these" />
            </CheckboxGrid>
          </Section>

          {/* ── Step 5: Tax regime ── */}
          <Section id="regime" eyebrow="Step 5" title="Tax regime" description="Special regimes can materially change the analysis, so this stays separate from normal residency questions.">
            <div className="profile-grid-2">
              <Select
                label="Beckham Law status"
                fieldKey="beckham_status"
                helper="Flat 24% on Spain-source income for qualifying new residents."
                options={[
                  { value: 'approved',     label: 'Already approved' },
                  { value: 'applied',      label: 'Application pending' },
                  { value: 'considering',  label: 'Considering it' },
                  { value: 'not_eligible', label: 'Not applicable' },
                  { value: 'unknown',      label: 'Not sure what this is' },
                ]}
              />
            </div>
          </Section>

          {/* ── Step 6: Goals ── */}
          <Section id="goals" eyebrow="Step 6" title="Goals & deadlines" description="This tells the product how urgent and detailed the assessment and chat responses should be.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="profile-grid-2">
                <Select label={`What ${isNonSelf ? 'are they' : 'are you'} trying to do?`} fieldKey="filing_goal" options={getSlotOptions('filing_goal')} />
                <Select label="Upcoming deadline?" fieldKey="urgency_deadline" options={[
                  { value: 'imminent',       label: 'Yes — within 30 days' },
                  { value: 'soon',           label: 'Yes — within 3 months' },
                  { value: 'planning_ahead', label: 'No — planning ahead' },
                  { value: 'past_deadline',  label: 'May have missed a deadline' },
                ]} />
              </div>
              <TextAreaField
                label="Prior year issues or open questions"
                value={form.prior_year_issues || ''}
                onChange={value => set('prior_year_issues', value)}
                placeholder="e.g. Late FBAR filing, missed Modelo 720, open audit…"
              />
            </div>
          </Section>

        </div>
      </main>

      {/* ── Fixed save bar ── */}
      <div className="profile-save-bar">
        <div className="profile-save-bar-inner">
          <Link to="/chat" className="btn-secondary">Cancel / go back</Link>
          <div className="u-row-md">
            {saved     && <span className="profile-saved-msg">✓ {subjectName ? `${subjectName}'s profile` : 'Profile'} saved</span>}
            {saveError && <span className="profile-error-msg">{saveError}</span>}
            <button className="btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      </div>

    </div>
  )
}